/**
 * P3-38: ResetWgt —— Reset 按钮 + karma/paragon/zebra 估算。
 *
 * 对齐 `js/time.js:1909-1961`。
 */
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { GameButton } from "../../components/GameButton";

interface ResetSnapshot {
  resetHtml: string;
}

function getSnapshot(game: GamePage | null): ResetSnapshot | null {
  if (!game) {
    return null;
  }
  let msg = t("time.reset.instructional");

  const prestige = game.getResetPrestige?.() ?? { paragonPoints: 0, karmaKittens: 0 };
  const paragonPoints = prestige.paragonPoints ?? 0;
  const karmaKittens = prestige.karmaKittens ?? 0;

  const stripe = 5;
  const karmaPointsPresent = game.getUnlimitedDR?.(game.karmaKittens, stripe) ?? 0;
  const karmaPointsAfter = game.getUnlimitedDR?.(karmaKittens, stripe) ?? 0;
  const karmaPoints = Math.floor((karmaPointsAfter - karmaPointsPresent) * 100) / 100;

  const bonus = game.prestige?.getResetBonusBreakdown?.(paragonPoints, karmaPoints) ?? {
    happiness: 0,
    storage: 0,
    production: 0,
  };

  msg +=
    "<br>" +
    t("time.reset.karma") +
    ": " +
    karmaPoints +
    " <span style='color:gray;'>(" +
    t("time.reset.bonus.happiness", [game.getDisplayValueExt?.(bonus.happiness * 100) ?? "0"]) +
    ")</span>";
  msg +=
    "<br>" +
    t("time.reset.paragon") +
    ": " +
    paragonPoints +
    " <span style='color:gray;'>(" +
    t("time.reset.bonus.storage", [game.getDisplayValueExt?.(bonus.storage * 100) ?? "0"]) +
    ", " +
    t("time.reset.bonus.production", [game.getDisplayValueExt?.(bonus.production * 100) ?? "0"]) +
    ")</span>";

  if (game.ironWill) {
    msg += "<br>" + t("time.reset.zebra") + ": " + game._getBonusZebras?.();
  }

  return { resetHtml: msg };
}

function snapEq(a: ResetSnapshot | null, b: ResetSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return a.resetHtml === b.resetHtml;
}

export default function ResetWgt() {
  const snapshot = useGameTick(
    (game) => getSnapshot(game as GamePage | null),
    snapEq,
  );

  if (!snapshot) {
    return null;
  }

  const game = getGame();

  return (
    <div>
      <GameButton
        name={t("menu.reset") ?? "Reset"}
        description={t("time.reset.desc") ?? ""}
        onClick={() => {
          game?.reset?.();
        }}
      />
      <div
        style={{ paddingTop: "20px" }}
        dangerouslySetInnerHTML={{ __html: snapshot.resetHtml }}
      />
    </div>
  );
}
